import { FieldValues } from 'react-hook-form'
import {
  DiscardFaxPayload,
  AttachFaxPayload,
  fetchLayoutIdPayload,
  splitFaxPayload,
  bulkAssignPayload,
  UpdateIntakePayload,
} from '../types'
import axiosInstance from '../utils/axiosInstance'
import { API_END_POINT, ENTITY_MANAGER_END_POINTS } from '../utils/environment'
import {
  buildProgramServiceHeaders,
  validateApiParams,
} from '../utils/functions'
import { getEntityNameById, resolveEntityName } from '../utils/entities.utils'

export const discardFaxById = async (
  payload: DiscardFaxPayload,
  programId?: string
) => {
  return await axiosInstance.patch(
    `${API_END_POINT.DELIVERY}/v1/communications/discard/${payload?.id}?reason=${payload?.reason}&reviewer=${payload?.reviewer}`,
    {},
    {
      headers: buildProgramServiceHeaders(programId),
    }
  )
}

export const attachCaseById = async (payload: AttachFaxPayload) => {
  return await axiosInstance.patch(
    `${API_END_POINT.DELIVERY}/v1/communications/attach/${payload?.faxId}/case/${payload?.caseId}?reason=${payload?.reason}&reviewer=${payload?.reviewer}`
  )
}

export const fetchEntityIdByNameCorrespondingToSelectedClient = async (
  entityName: string
) => {
  return await axiosInstance.get(
    `${API_END_POINT.ENTITY_MANAGER}entities/name/${entityName}`
  )
}

export const fetchFaxIdCorrespondingData = async (id: number) => {
  return await axiosInstance.get(
    `${API_END_POINT.DELIVERY}/v1/communications/${id}`
  )
}

interface ObjectType {
  [name: string]: string | string[]
}

export interface IntakeTypes {
  entity_name: string
  select: string[]
  related_entities?: {
    [entityName: string]: {
      select: string[]
    }
  }
}

export const fetchWebIdCorrespondingData = async (
  id: number,
  payload: ObjectType
) => {
  validateApiParams(
    { id },
    {
      endpoint: `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/hcp_portal_entity/${id}`,
      method: 'POST',
    }
  )
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/hcp_portal_entity/${id}`,
    payload
  )
}

export const fetchCommunicationIdCorrespondingData = async (
  id: number,
  payload: IntakeTypes
) => {
  const resolvedEntityName = resolveEntityName(
    payload['entity_name'],
    undefined
  )
  validateApiParams(
    {
      id: id,
      entity_name: resolvedEntityName,
    },
    {
      endpoint: `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${resolvedEntityName}/${id}`,
      method: 'POST',
    }
  )
  return await axiosInstance.post(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${resolvedEntityName}/${id}`,
    { ...payload, entity_name: resolvedEntityName }
  )
}

export const fetchFaxUrl = async (id: number) => {
  validateApiParams(
    { id },
    {
      endpoint: `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/${id}`,
      method: 'GET',
    }
  )
  return await axiosInstance.get(
    `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/${id}`
  )
}

export const fetchLayoutIdApi = async (payload: fetchLayoutIdPayload) => {
  const endpoint = `${API_END_POINT.LAYOUT_MANAGER}layout?type=${payload?.mode}&entityId=${payload?.entityId}&defaultValue=true`
  validateApiParams(
    { mode: payload?.mode, entityId: payload?.entityId },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const changeFaxStatus = async (
  entityName: string,
  communicationId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const resolvedEntityName = resolveEntityName(entityName, entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${resolvedEntityName}/${communicationId}`
  validateApiParams(
    { resolvedEntityName, communicationId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(
    endpoint,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: data,
    },
    { params: { entityId, layoutId } }
  )
}

export const splitFaxApi = async (payload: splitFaxPayload) => {
  return await axiosInstance.post(
    `${API_END_POINT.DELIVERY}/v1/communications/split/${payload?.communicationId}/${payload?.userId}`,
    payload?.splitArray
  )
}

export const splitCaseDocumentApi = async (
  caseId: number,
  path: string,
  payload: { pageRanges: number[]; documentName: string }[]
) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}v1/split/case/documents/${caseId}?relativePath=${path}`,
    payload
  )
}

export const bulkAssignToAgent = async (payload: bulkAssignPayload) => {
  return await axiosInstance.patch(
    `${API_END_POINT.CORE}queueAgent/assign?queueId=${payload?.queueId}&userId=${payload?.userId}&recordIds=${payload?.recordsIds}`
  )
}

export const fetchHcpFormPdfUrl = async (enrollment_id: string) => {
  const endpoint = `${API_END_POINT.CORE}hcp/microsite/econfig/enrollment/${enrollment_id}/pdf?flag=signed`
  validateApiParams(
    { enrollment_id },
    {
      endpoint,
      method: 'GET',
    }
  )
  return await axiosInstance.get(endpoint)
}

export const discardWebIntakeApi = async (
  communicationId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const resolvedEntityName = getEntityNameById(entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${resolvedEntityName}/${communicationId}?entityId=${entityId}&layoutId=${layoutId}`
  validateApiParams(
    { resolvedEntityName, communicationId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: data,
  })
}

export const patchAttachForHcpEntity = async (
  ids: string[],
  entityId: string,
  layoutId: string,
  caseId: string | number,
  enrollmentStatusId: string
) => {
  const resolvedEntityName = getEntityNameById(entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/${resolvedEntityName}?ids=${ids}`
  validateApiParams(
    { entityId, resolvedEntityName, layoutId, caseId, enrollmentStatusId },
    {
      endpoint,
      method: 'PATCH',
    }
  )
  return await axiosInstance.patch(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: {
      case_id: caseId,
      enrollment_status_id: enrollmentStatusId,
    },
  })
}

export const fetchDocumentIdForHcpEntity = async (
  entityId: string,
  enrollmentId: string
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/web_enrollment_document`
  validateApiParams(
    { entityId, enrollmentId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    entity_id: entityId,
    select: ['id', 'document', 'enrollment_id'],
    page: 0,
    size: 10,
    filters: [
      {
        criteria: [
          {
            field_name: 'enrollment_id',
            values: [enrollmentId],
            operation: 'EQUALS',
          },
        ],
      },
    ],
  })
}

export const fetchCaseDocumentIdForHcpEntity = async (
  entityId: string,
  layoutId: string,
  caseId: string | number | undefined,
  documentId: string | undefined,
  enrollmentId: string | undefined,
  caseDocmentTypeId?: number
) => {
  const resolvedEntityName = getEntityNameById(entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/${resolvedEntityName}`
  validateApiParams(
    { entityId, resolvedEntityName, layoutId },
    {
      endpoint,
      method: 'POST',
    }
  )
  return await axiosInstance.post(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: {
      case_id: caseId,
      document_id: Number(documentId),
      name: enrollmentId,
      case_document_type: { id: caseDocmentTypeId, _state: 'ADDED' },
    },
  })
}

export const fetchPrintDocument = async (ids: string) => {
  return await axiosInstance.get(
    `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/bulk-print?recordIds=${ids}`
  )
}

export const markCompleteStatusPrintDoc = async (ids: string) => {
  return await axiosInstance.patch(
    `${API_END_POINT.DELIVERY}/v1/communications/completeStatus?recordIds=${ids}`
  )
}

export const completedFaxIntakeApi = async (
  communicationId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/communications/${communicationId}`
  validateApiParams(
    { communicationId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
    },
    data: data,
  })
}

export const updateEntityInstanceApi = async (
  entityName: string,
  recordId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues
) => {
  const resolvedEntityName = resolveEntityName(entityName, entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${resolvedEntityName}/${recordId}`
  validateApiParams(
    { resolvedEntityName, recordId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(
    endpoint,
    {
      meta_data: {
        entity_id: entityId,
        layout_id: layoutId,
      },
      data: data,
    },
    { params: { entityId, layoutId } }
  )
}

export const updateEntityInstanceCorrespondingToCaseId = async (
  tableName: string,
  recordId: number,
  entityId: number,
  layoutId: number,
  data: FieldValues,
  relationIdentifier?: string
) => {
  const resolvedEntityName = getEntityNameById(entityId)
  const endpoint = `${API_END_POINT.ENTITY_MANAGER_V3}${ENTITY_MANAGER_END_POINTS.ENTITY_DATA}/${resolvedEntityName}/${recordId}/${tableName}`
  validateApiParams(
    { resolvedEntityName, tableName, recordId, entityId, layoutId },
    {
      endpoint,
      method: 'PUT',
    }
  )
  return await axiosInstance.put(endpoint, {
    meta_data: {
      entity_id: entityId,
      layout_id: layoutId,
      relation_identifier: relationIdentifier,
    },
    data: data,
  })
}

export const updateIntake = async (
  communicationId = '',
  payload: UpdateIntakePayload
) => {
  return await axiosInstance.put(
    `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/entities/${communicationId}`,
    payload
  )
}

export const detachIntake = async (communicationId = '', caseId = '') => {
  return await axiosInstance.patch(
    `${API_END_POINT.TEMPLATE}/v1/communication-logs/${communicationId}/detach-case/${caseId}`
  )
}
export const auditMultiChannelSubmissionsApi = async (payload: {
  existing_case_id: number
  new_case_id: number
  communication_id: number
  case_document_type_id: number
}) => {
  return await axiosInstance.post(
    `${API_END_POINT.CORE}audit-multi-channel-submissions`,
    payload
  )
}
